import { Color, Spacing } from '@persona-id/design-tokens';
import { TrashIcon } from '@persona-id/design-tokens/icons';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled, { css } from 'styled-components';

import ModalWrapper from 'dashboard/components/ModalWrapper';
import { GovernmentIdDetailsTable } from 'dashboard/components/Simulate/GovernmentIdDetailsTable';
import { VerificationChecksTable } from 'dashboard/components/Simulate/VerificationChecksTable';
import { VerificationMatchRequirementChecksTable } from 'dashboard/components/Simulate/VerificationMatchRequirementChecksTable';
import { DeletePresetModal } from 'dashboard/components/SimulatePreset/DeletePresetModal';
import useArchiveSimulatePreset from 'dashboard/components/SimulatePreset/hooks/useArchiveSimulatePreset';
import LoadingIndicator from 'dashboard/components-lib/LoadingIndicator';
import MaximizablePanel from 'dashboard/components-lib/MaximizablePanel';
import { getDefaultPreset } from 'dashboard/lib/simulate/presets/utils';
import {
  SimulateCustomPresetVerification,
  SimulatePresetVerificationAttributes,
  StubsWithOptionalId,
} from 'dashboard/models/simulate/preset';
import {
  CheckStub,
  GovernmentIdExtractions,
  MatchRequirementsCheckStub,
} from 'dashboard/models/simulate/stubs';
import { VerificationTemplateVersion } from 'dashboard/models/verification-template-version';
import Button from 'design-system/components/Button';
import Card from 'design-system/components/Card/Card';
import Spacer from 'design-system/components/Spacer';
import { Paragraph } from 'design-system/components/Text';
import Tooltip from 'design-system/components/Tooltip';
import hoverableMixin from 'design-system/mixins/hoverable';
import { paragraphMixins } from 'design-system/mixins/text';
import { FontWeight } from 'design-system/variables/text';
import { TransitionDuration } from 'design-system/variables/transitions';
import useToggle from 'lib/hooks/useToggle';

const FooterContainer = styled.div`
  align-items: center;
  justify-content: space-between;
  display: flex;
`;

const FooterFlexContainer = styled.div`
  display: flex;
  gap: ${Spacing[2]};
`;

const StyledTitleText = styled.div`
  ${paragraphMixins.lgBold}
`;

const PreviewContainer = styled.div`
  padding: ${Spacing[4]};
  min-height: 300px;
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  flex: none;
  overflow-y: auto;
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;
  height: 100%;
`;

const LeftColumn = styled.div`
  display: grid;
  position: relative;
  left: -${Spacing[3]};
  grid-template-columns: 1fr;
  grid-gap: ${Spacing[3]};
  flex: none;
  height: 100%;
  max-height: 400px;
  width: 300px;
  overflow-y: auto;
  padding: ${Spacing[3]};
  background-color: ${({ theme }) => theme.color.background.primary};
  border-top-left-radius: inherit;
  border-bottom-left-radius: inherit;
`;

const RightColumnWrapper = styled.div`
  display: flex;
  flex-direction: column;
  position: relative;
  flex: auto;
  overflow: hidden;
  border-radius: inherit;
  margin-left: -${Spacing[3]};
`;

const RightColumn = styled.div`
  display: flex;
  flex: auto;
  width: 100%;
  height: 400px;
  background-color: ${Color.CoreSlate100};
  align-items: center;
  justify-content: center;
`;

const CardDivider = styled.div`
  ${paragraphMixins.xs}
  text-transform: uppercase;
  color: ${Color.TextSecondary};
  font-weight: ${FontWeight.Medium};
`;

const StyledCard = styled(Card)`
  padding: ${Spacing[4]};
  margin-bottom: ${Spacing[0]};
  position: relative;
  border: solid;
  border-color: ${Color.BorderHairline};
  border-width: 1px;
`;

interface FooterProps {
  onChoose: () => void;
  onCancel: () => void;
  disableArchive: boolean;
  showDeletePresetModal: () => void;
}

const Footer = ({ onChoose, onCancel, disableArchive, showDeletePresetModal }: FooterProps) => {
  const portalRef = useRef(null);

  return (
    <FooterContainer ref={portalRef}>
      {/* Have to use a portal container since this exists on a panel with a higher z-index than the default tooltip */}
      <Tooltip
        portal
        portalProps={{ container: portalRef.current }}
        side='top'
        disabled={!disableArchive}
        iconTrigger={
          <Button
            id='footer__more'
            variant='danger'
            width='icon'
            onClick={showDeletePresetModal}
            disabled={disableArchive}
          >
            <TrashIcon />
          </Button>
        }
      >
        <Paragraph>Cannot delete example presets.</Paragraph>
      </Tooltip>
      <FooterFlexContainer>
        <Button id='footer__choose-preset' size='sm' width='auto' onClick={onChoose}>
          Choose Preset
        </Button>
        <Button id='footer__cancel' variant='basic' size='sm' width='auto' onClick={onCancel}>
          Cancel
        </Button>
      </FooterFlexContainer>
    </FooterContainer>
  );
};

const StyledPresetCard = styled(Card)<{
  selected?: boolean;
}>`
  display: flex;
  ${hoverableMixin}
  &:hover,
  &:active,
  &:focus {
    ${({ selected }) =>
      !selected &&
      css`
        outline: 2px solid ${({ theme }) => theme.color.primary.brand200};
      `}
  }
  transition: transform ${TransitionDuration.Medium};
  margin-bottom: 0px;
  outline: 1px solid ${({ theme }) => theme.color.border.primary};
  ${({ selected }) =>
    selected &&
    css`
      outline: 1px solid ${({ theme }) => theme.color.primary.brand600};
    `}
`;

const StyledCardBody = styled(Card.Body)`
  display: flex;
  flex-direction: row;
  flex: auto;
  padding: ${Spacing[4]};
`;

const PresetContentWrapper = styled.div`
  display: flex;
  width: 100%;
  flex-direction: column;
  margin-left: ${Spacing[3]};
  gap: ${Spacing[1]};
`;

const CardDescription = styled(Paragraph)`
  line-height: 22px;
  text-overflow: ellipsis;
  display: -webkit-box;
  overflow: hidden;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
`;

export interface PresetOptionCardProps {
  name: string;
  title: string;
  description: string | undefined;
  selected: boolean;
  onClick: () => void;
}

const PresetOptionCard = ({
  name,
  title,
  description,
  selected,
  onClick,
}: PresetOptionCardProps) => {
  return (
    <StyledPresetCard selected={selected}>
      <StyledCardBody aria-label={name} onClick={onClick}>
        <PresetContentWrapper>
          <Paragraph size='sm' color='primary'>
            {title}
          </Paragraph>
          <CardDescription size='sm'>{description}</CardDescription>
        </PresetContentWrapper>
      </StyledCardBody>
    </StyledPresetCard>
  );
};

interface StubsPreviewProps {
  preset: SimulatePresetVerificationAttributes;
  verificationTemplateVersion: VerificationTemplateVersion;
}

const StubsPreview = ({ preset, verificationTemplateVersion }: StubsPreviewProps) => {
  if (preset == null) {
    return <></>;
  }

  const verificationStubs = preset['stubs']['verification-stubs'];

  let statusCheckStubs: CheckStub[] | undefined;
  let matchRequirementCheckStubs: MatchRequirementsCheckStub[] | undefined;
  let governmentIdExtractionStubs: GovernmentIdExtractions | undefined;

  if ('check-stubs' in verificationStubs) {
    const checkStubs = verificationStubs['check-stubs'];
    statusCheckStubs = checkStubs.filter((checkStub) => 'status' in checkStub) as CheckStub[];
    matchRequirementCheckStubs = checkStubs.filter(
      (checkStub) => 'match-requirement-stubs' in checkStub,
    ) as MatchRequirementsCheckStub[];
  }
  if ('document-stubs' in preset['stubs']) {
    governmentIdExtractionStubs = preset['stubs']['document-stubs']?.['extractions'];
  }

  return (
    <StyledCard>
      <StyledTitleText>{preset.name}</StyledTitleText>
      <Paragraph>{preset.description}</Paragraph>
      {statusCheckStubs && (
        <>
          <Spacer height={2} />
          <VerificationChecksTable checkStubs={statusCheckStubs} />
        </>
      )}
      {matchRequirementCheckStubs && matchRequirementCheckStubs.length > 0 && (
        <>
          <Spacer height={2} />
          <VerificationMatchRequirementChecksTable
            verificationTemplateVersion={verificationTemplateVersion}
            matchRequirementCheckStubs={matchRequirementCheckStubs}
          />
        </>
      )}
      {governmentIdExtractionStubs && (
        <>
          <Spacer height={2} />
          <GovernmentIdDetailsTable extractionStubs={governmentIdExtractionStubs} />
        </>
      )}
    </StyledCard>
  );
};

interface VerificationPresetsPanelProps {
  verificationTemplateVersion: VerificationTemplateVersion;
  examplePresets: SimulatePresetVerificationAttributes[];
  customPresets: SimulateCustomPresetVerification[];
  loadingCustomPresets: boolean;
  reloadPresets: () => Promise<void>;
  trackEvent: (eventName: string, props?: Record<string, any>) => void;
  selectedPreset: StubsWithOptionalId | null;
  setSelectedPreset: (preset: StubsWithOptionalId | null) => void;
  removeCustomPreset: (preset: StubsWithOptionalId) => void;
  offsetElementId?: string;
  onOutsideClick: () => void;
}

export const VerificationPresetsPanel = ({
  verificationTemplateVersion,
  examplePresets,
  customPresets,
  loadingCustomPresets,
  reloadPresets,
  selectedPreset,
  setSelectedPreset,
  removeCustomPreset,
  offsetElementId,
  onOutsideClick,
}: VerificationPresetsPanelProps) => {
  let defaultPreviewedPreset;
  if (selectedPreset) {
    // If a preset is currently selected, preview that preset
    defaultPreviewedPreset = selectedPreset;
  } else {
    // Otherwise preview the default
    defaultPreviewedPreset = getDefaultPreset(customPresets, examplePresets);
  }

  const [previewedPreset, setPreviewedPreset] =
    useState<StubsWithOptionalId>(defaultPreviewedPreset);

  // Whenever the previewed preset does not exist, namely following archival of the currently selected preset,
  // we reselect a default
  useEffect(() => {
    if (
      previewedPreset.id &&
      !customPresets.map((preset) => preset.id).includes(previewedPreset.id)
    ) {
      setPreviewedPreset(getDefaultPreset(customPresets, examplePresets));
    }
  }, [customPresets]);

  const [
    hasOpenDeletePresetModal,
    { enable: showDeletePresetModal, disable: hideDeletePresetModal },
  ] = useToggle(false);
  const { handleArchiveSimulatePreset, loadingArchiveSimulatePreset } = useArchiveSimulatePreset();

  const handleArchivePresetAndReload = useCallback(
    async (presetId: string) => {
      await handleArchiveSimulatePreset(presetId);
      await reloadPresets();
    },
    [handleArchiveSimulatePreset, reloadPresets],
  );

  const cardsToShow = useMemo(() => {
    return (
      <>
        <CardDivider>Custom</CardDivider>
        {loadingCustomPresets ? (
          <LoadingIndicator />
        ) : customPresets.length > 0 ? (
          customPresets.map((preset) => {
            return (
              <PresetOptionCard
                name={`${preset.attributes.name} Preset Option Card`}
                key={`preset-option-card__${preset.attributes.name}`}
                title={preset.attributes.name}
                description={preset.attributes.description}
                onClick={() => setPreviewedPreset({ ...preset.attributes, id: preset.id })}
                selected={
                  // We do not prevent name collisions between custom presets and example presets,
                  // so if a custom preset and an example preset have the same name, we use the presence
                  // of preset id to determine which preset is currently selected
                  preset.attributes.name === previewedPreset.name && previewedPreset.id != null
                }
              />
            );
          })
        ) : (
          <Paragraph>Custom presets will appear here.</Paragraph>
        )}
        <CardDivider>Examples</CardDivider>
        {examplePresets.map((preset) => {
          return (
            <PresetOptionCard
              name={`${preset.name} Preset Option Card`}
              key={`preset-option-card__${preset.name}`}
              title={preset.name}
              description={preset.description}
              onClick={() => setPreviewedPreset(preset)}
              selected={
                // We do not prevent name collisions between custom presets and example presets,
                // so if a custom preset and an example preset have the same name, we use the presence
                // of preset id to determine which preset is currently selected
                preset.name === previewedPreset.name && previewedPreset.id == null
              }
            />
          );
        })}
      </>
    );
  }, [previewedPreset]);

  return (
    <MaximizablePanel
      showHairlines
      title='Choose a preset to simulate'
      footer={
        <Footer
          onChoose={() => {
            setSelectedPreset(previewedPreset);
            onOutsideClick();
          }}
          onCancel={onOutsideClick}
          disableArchive={previewedPreset.id == null}
          showDeletePresetModal={showDeletePresetModal}
        />
      }
      onOutsideClickAndEscPress={() => {
        // Because modals use portal to mount the modal on a different parent element,
        // the outside click handler does not recognize a modal click as being part of
        // wrapped subtree. Therefore, if we know the modal is open, then any click
        // must be on the modal and we should skip the outside click callback
        if (!hasOpenDeletePresetModal) {
          onOutsideClick();
        }
      }}
      offsetElementId={offsetElementId}
    >
      <ModalWrapper isOpen={hasOpenDeletePresetModal} handleCloseModal={hideDeletePresetModal}>
        <DeletePresetModal
          handleArchivePreset={async () => {
            if (previewedPreset.id) {
              await handleArchivePresetAndReload(previewedPreset.id);
              removeCustomPreset(previewedPreset);
            }
            hideDeletePresetModal();
          }}
          isArchivingPreset={loadingArchiveSimulatePreset}
          hideModal={hideDeletePresetModal}
        />
      </ModalWrapper>
      <ContentWrapper>
        <LeftColumn>{cardsToShow}</LeftColumn>
        <RightColumnWrapper>
          <RightColumn>
            <PreviewContainer aria-label='Verification Presets Panel Preview'>
              <StubsPreview
                preset={previewedPreset}
                verificationTemplateVersion={verificationTemplateVersion}
              />
            </PreviewContainer>
          </RightColumn>
        </RightColumnWrapper>
      </ContentWrapper>
    </MaximizablePanel>
  );
};
